// client/src/components/BusinessOnboardingFlow.js
// Phase 1: Complete Brand DNA profile (5 sections)
import React, { useState, useEffect, useRef } from 'react';
import { businessAPI } from '../../../services/businessAPI';

const BUSINESS_TYPES = [
  'Cafe / Coffee Shop', 'Restaurant / Food Service', 'Retail Store',
  'Creative Studio / Art Gallery', 'Fitness / Wellness / Gym',
  'Professional Services / Consulting', 'Education / Training / Coaching',
  'Entertainment / Events', 'Beauty / Salon / Spa', 'Healthcare / Medical',
  'Technology / Software', 'Real Estate / Property',
  'Marketing / Advertising Agency', 'Photography / Videography',
  'Home Services / Trades', 'Other'
];

const INDUSTRIES = [
  'Hospitality & Food Service', 'Retail & E-commerce', 'Health & Wellness',
  'Professional Services', 'Education & Training', 'Entertainment & Events',
  'Beauty & Personal Care', 'Technology & Software', 'Real Estate & Property',
  'Marketing & Creative Services', 'Home Services & Trades', 'Healthcare & Medical',
  'Fitness & Sports', 'Arts & Culture', 'Tourism & Travel', 'Other'
];

const PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: 'fab fa-instagram' },
  { id: 'facebook', name: 'Facebook', icon: 'fab fa-facebook' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'fab fa-linkedin' },
  { id: 'tiktok', name: 'TikTok', icon: 'fab fa-tiktok' },
  { id: 'twitter', name: 'Twitter/X', icon: 'fab fa-twitter' },
  { id: 'youtube', name: 'YouTube', icon: 'fab fa-youtube' },
  { id: 'email', name: 'Email', icon: 'fas fa-envelope' },
  { id: 'googleBusiness', name: 'Google Business', icon: 'fas fa-map-marker-alt' }
];

// Sub-specialty chips shown below businessType — gives AI precise visual context
const SPECIALTY_MAP = {
  'Beauty / Salon / Spa': [
    'Skin Clinic / Aesthetics', 'Hair Salon', 'Nail Studio',
    'Spa & Massage', 'Barbershop', 'Brows & Lashes', 'Medical Aesthetics',
  ],
  'Healthcare / Medical': [
    'GP / Family Practice', 'Skin / Dermatology', 'Dental / Orthodontics',
    'Physiotherapy', 'Mental Health', 'Optometry', 'Pharmacy',
  ],
  'Fitness / Wellness / Gym': [
    'Gym / CrossFit', 'Yoga Studio', 'Pilates', 'Personal Training',
    'Sports Club', 'Martial Arts',
  ],
  'Restaurant / Food Service': [
    'Restaurant', 'Takeaway / Delivery', 'Catering', 'Food Truck', 'Bakery',
  ],
  'Cafe / Coffee Shop': [
    'Café / Coffee Bar', 'Bakery Café', 'Brunch Spot', 'Juice & Smoothie Bar',
  ],
  'Retail Store': [
    'Clothing & Fashion', 'Electronics', 'Home & Gift', 'Jewellery',
    'Books & Stationery', 'Sports & Outdoors',
  ],
  'Professional Services / Consulting': [
    'Accountancy', 'Legal', 'HR & Recruitment', 'Financial Advisory', 'Management Consulting',
  ],
  'Education / Training / Coaching': [
    'School / Academy', 'Tutoring', 'Corporate Training', 'Life Coaching', 'Online Courses',
  ],
  'Technology / Software': [
    'SaaS / Software', 'App Development', 'IT Support', 'Web Design', 'Digital Agency',
  ],
  'Real Estate / Property': [
    'Estate Agency', 'Property Management', 'Mortgage Broking', 'Commercial Property',
  ],
  'Home Services / Trades': [
    'Plumbing', 'Electrical', 'Landscaping / Gardening', 'Cleaning', 'Renovation / Building',
  ],
  'Photography / Videography': [
    'Wedding Photography', 'Commercial Photography', 'Video Production', 'Portrait Studio',
  ],
};

const CAPTION_TONES = [
  { id: 'professional', label: 'Professional', icon: 'fas fa-briefcase' },
  { id: 'conversational', label: 'Conversational', icon: 'fas fa-comments' },
  { id: 'inspiring', label: 'Inspiring', icon: 'fas fa-star' },
  { id: 'educational', label: 'Educational', icon: 'fas fa-graduation-cap' },
  { id: 'promotional', label: 'Promotional', icon: 'fas fa-bullhorn' }
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function BusinessOnboardingFlow({ onSave, businessData, hasExistingCalendar }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(1);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingStyle, setUploadingStyle] = useState(-1); // index of slot being uploaded, -1 = none
  const logoInputRef = useRef(null);
  const styleInputRefs = [useRef(null), useRef(null), useRef(null)];

  // Track original calendar-affecting values so we can detect changes
  const [savedReelsPriority, setSavedReelsPriority] = useState('');
  const [savedPostingFrequency, setSavedPostingFrequency] = useState('');
  // Calendar reset warning modal
  const [showCalendarResetWarning, setShowCalendarResetWarning] = useState(false);

  // Core business fields (existing model columns)
  const [formData, setFormData] = useState({
    businessName: '',
    businessType: '',
    businessTypeOther: '',
    industry: '',
    industryOther: '',
    location: '',
    website: '',
    preferredPlatforms: []
  });

  // Brand DNA fields (stored in brandDna JSONB column)
  const [brandDna, setBrandDna] = useState({
    // Section 1 extras
    businessSpecialty: '', // e.g. 'Skin Clinic / Aesthetics' — narrows broad businessType for AI
    primaryService: '',
    idealCustomer: { ageRange: '', gender: '', lifestyle: '', location: '' },
    // Section 2
    logoUrl: '',
    primaryColour: '#000000',
    secondaryColour: '#666666',
    accentColour: '#3B82F6',
    fontStyle: '',
    // Section 3
    styleExamples: ['', '', ''], // now stores image URLs from uploads
    contentPreference: '', // image_led or text_led
    // Section 4 extras
    existingMediaUrl: '',
    reelsPriority: '', // reels_first, static_first, both
    postingFrequency: '', // daily, 3x_week, weekly
    // Section 5
    captionTone: [], // now multi-select array
    useEmojis: '', // yes, no, occasionally
    contentRestrictions: ''
  });

  useEffect(() => {
    if (businessData) {
      setFormData(prev => ({
        ...prev,
        businessName: businessData.businessName || '',
        businessType: businessData.businessType || '',
        businessTypeOther: businessData.businessTypeOther || '',
        industry: businessData.industry || '',
        industryOther: businessData.industryOther || '',
        location: businessData.location || '',
        website: businessData.website || '',
        preferredPlatforms: businessData.preferredPlatforms || []
      }));
      if (businessData.brandDna && typeof businessData.brandDna === 'object') {
        setBrandDna(prev => ({
          ...prev,
          ...businessData.brandDna,
          // Ensure captionTone is always an array (migrate from old string value)
          captionTone: Array.isArray(businessData.brandDna.captionTone)
            ? businessData.brandDna.captionTone
            : businessData.brandDna.captionTone
              ? [businessData.brandDna.captionTone]
              : []
        }));
        // Capture saved values for change detection
        setSavedReelsPriority(businessData.brandDna.reelsPriority || '');
        setSavedPostingFrequency(businessData.brandDna.postingFrequency || '');
      }
    }
  }, [businessData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleBrandDna = (field, value) => {
    setBrandDna(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleIdealCustomer = (field, value) => {
    setBrandDna(prev => ({
      ...prev,
      idealCustomer: { ...prev.idealCustomer, [field]: value }
    }));
  };

  const togglePlatform = (platformId) => {
    setFormData(prev => {
      const current = prev.preferredPlatforms;
      const updated = current.includes(platformId)
        ? current.filter(p => p !== platformId)
        : [...current, platformId];
      return { ...prev, preferredPlatforms: updated };
    });
    setError(null);
  };

  const toggleCaptionTone = (toneId) => {
    setBrandDna(prev => {
      const current = prev.captionTone;
      const updated = current.includes(toneId)
        ? current.filter(t => t !== toneId)
        : [...current, toneId];
      return { ...prev, captionTone: updated };
    });
    setError(null);
  };

  const selectAllTones = () => {
    const allIds = CAPTION_TONES.map(t => t.id);
    const allSelected = allIds.every(id => brandDna.captionTone.includes(id));
    setBrandDna(prev => ({ ...prev, captionTone: allSelected ? [] : allIds }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingLogo(true);
      const result = await businessAPI.uploadLogo(file);
      if (result.success) {
        handleBrandDna('logoUrl', result.logoUrl);
      }
    } catch (err) {
      console.error('Logo upload error:', err);
      setError('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleStyleImageUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingStyle(index);
      const result = await businessAPI.uploadStyleExample(file);
      if (result.success) {
        const updated = [...brandDna.styleExamples];
        updated[index] = result.imageUrl;
        handleBrandDna('styleExamples', updated);
      }
    } catch (err) {
      console.error('Style example upload error:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploadingStyle(-1);
    }
  };

  const removeStyleImage = (index) => {
    const updated = [...brandDna.styleExamples];
    updated[index] = '';
    handleBrandDna('styleExamples', updated);
  };

  const isFormValid = () => {
    return (
      formData.businessName.trim() &&
      formData.businessType &&
      (formData.businessType !== 'Other' || formData.businessTypeOther.trim()) &&
      formData.industry &&
      (formData.industry !== 'Other' || formData.industryOther.trim()) &&
      formData.location.trim() &&
      formData.preferredPlatforms.length >= 1
    );
  };

  const calendarFieldsChanged =
    hasExistingCalendar &&
    savedReelsPriority !== '' && // only if there was a saved value (i.e. editing, not initial setup)
    (brandDna.reelsPriority !== savedReelsPriority || brandDna.postingFrequency !== savedPostingFrequency);

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setError('Please complete all required fields in Section 1.');
      setActiveSection(1);
      return;
    }
    // If calendar-affecting fields changed, prompt before saving
    if (calendarFieldsChanged) {
      setShowCalendarResetWarning(true);
      return;
    }
    await doSave(false);
  };

  const doSave = async (resetPhase2) => {
    try {
      setSaving(true);
      setError(null);
      await businessAPI.saveProfile({ ...formData, brandDna });
      await businessAPI.markPhase1Complete();
      if (resetPhase2) {
        await businessAPI.deleteStrategy();
      }
      onSave();
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
      setShowCalendarResetWarning(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    return `${API_URL}${url}`;
  };

  // Old images saved to Railway disk are now dead (ephemeral filesystem wiped on redeploy).
  // Detect them so we can show a re-upload prompt instead of a blank broken image.
  const isDeadPath = (url) => url && !url.startsWith('data:') && !url.startsWith('http');

  // Shared styles
  const inputClass = 'w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white placeholder-slate-400';
  const labelClass = 'block text-xs font-semibold text-slate-600 mb-1';
  const sectionComplete = (num) => {
    if (num === 1) return formData.businessName && formData.businessType && formData.industry && formData.location && formData.preferredPlatforms.length >= 1;
    if (num === 2) return true; // Visual Brand is optional
    if (num === 3) return brandDna.postingFrequency && brandDna.captionTone.length > 0;
    return false;
  };

  const SECTIONS = [
    { num: 1, title: 'Business Identity', icon: 'fas fa-building', color: 'blue' },
    { num: 2, title: 'Visual Brand', icon: 'fas fa-palette', color: 'purple' },
    { num: 3, title: 'Platforms & Voice', icon: 'fas fa-share-alt', color: 'blue' },
  ];

  return (
    <>
    {/* Calendar Reset Confirmation Modal */}
    {showCalendarResetWarning && (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-calendar-times text-red-500 text-xl"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Reset Content Calendar?</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">This cannot be undone.</p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-5 space-y-1">
            <p className="text-[11px] font-semibold text-amber-800">
              <i className="fas fa-exclamation-triangle mr-1.5"></i>You changed:
            </p>
            {brandDna.reelsPriority !== savedReelsPriority && (
              <p className="text-[11px] text-amber-700 pl-4">
                • Format Priority: <span className="font-semibold">{savedReelsPriority.replace(/_/g, ' ')}</span> → <span className="font-semibold text-blue-700">{brandDna.reelsPriority.replace(/_/g, ' ')}</span>
              </p>
            )}
            {brandDna.postingFrequency !== savedPostingFrequency && (
              <p className="text-[11px] text-amber-700 pl-4">
                • Posting Frequency: <span className="font-semibold">{savedPostingFrequency.replace(/_/g, ' ')}</span> → <span className="font-semibold text-blue-700">{brandDna.postingFrequency.replace(/_/g, ' ')}</span>
              </p>
            )}
            <p className="text-[11px] text-amber-700 mt-1">
              Your current Phase 2 strategy and content calendar will be deleted. You will need to regenerate a new calendar in Phase 2.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowCalendarResetWarning(false)} disabled={saving}
              className="flex-1 py-2.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all">
              Cancel
            </button>
            <button onClick={() => doSave(true)} disabled={saving}
              className="flex-1 py-2.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all flex items-center justify-center gap-1.5">
              {saving
                ? <><i className="fas fa-spinner fa-spin text-[10px]"></i> Saving...</>
                : <><i className="fas fa-trash text-[10px]"></i> Save & Reset Calendar</>
              }
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <i className="fas fa-dna text-white text-sm"></i>
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Brand DNA Profile</h1>
            <p className="text-xs text-slate-500">Build your complete visual identity for AI-powered content</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs flex items-center">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><i className="fas fa-times"></i></button>
        </div>
      )}

      {/* Section Navigation */}
      <div className="flex items-center gap-1 mb-4 bg-slate-100 rounded-lg p-1">
        {SECTIONS.map((s) => (
          <button key={s.num} onClick={() => setActiveSection(s.num)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[11px] font-semibold transition-all ${
              activeSection === s.num
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}>
            {sectionComplete(s.num) ? (
              <i className="fas fa-check-circle text-green-500 text-[10px]"></i>
            ) : (
              <i className={`${s.icon} text-[10px]`}></i>
            )}
            <span className="hidden sm:inline">{s.title}</span>
            <span className="sm:hidden">{s.num}</span>
          </button>
        ))}
      </div>

      {/* ============ SECTION 1: Business Identity ============ */}
      {activeSection === 1 && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold">1</div>
              <h2 className="text-sm font-bold text-slate-800">Business Identity</h2>
              <span className="text-[10px] text-slate-400 ml-auto">Core details that define your business</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Business Name <span className="text-red-500">*</span></label>
                <input type="text" value={formData.businessName} onChange={(e) => handleChange('businessName', e.target.value)}
                  placeholder="e.g. The Corner Cafe" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Business Type <span className="text-red-500">*</span></label>
                <select value={formData.businessType} onChange={(e) => {
                  handleChange('businessType', e.target.value);
                  handleBrandDna('businessSpecialty', ''); // reset specialty when type changes
                }} className={inputClass}>
                  <option value="">Select type...</option>
                  {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                {formData.businessType === 'Other' && (
                  <input type="text" value={formData.businessTypeOther} onChange={(e) => handleChange('businessTypeOther', e.target.value)}
                    placeholder="Specify type" className={`${inputClass} mt-2`} />
                )}
                {/* Specialty chips — appear for relevant business types, 1 tap */}
                {SPECIALTY_MAP[formData.businessType] && (
                  <div className="mt-2">
                    <p className="text-[10px] text-slate-500 mb-1.5 font-medium">
                      <i className="fas fa-crosshairs text-blue-400 mr-1"></i>
                      What's your specialty? <span className="text-slate-400 font-normal">(helps AI generate accurate images)</span>
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {SPECIALTY_MAP[formData.businessType].map(sp => (
                        <button
                          key={sp}
                          type="button"
                          onClick={() => handleBrandDna('businessSpecialty', brandDna.businessSpecialty === sp ? '' : sp)}
                          className={`px-2.5 py-1 text-[10px] font-semibold rounded-full border transition-all ${
                            brandDna.businessSpecialty === sp
                              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                              : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-600'
                          }`}
                        >
                          {brandDna.businessSpecialty === sp && <i className="fas fa-check mr-1 text-[8px]"></i>}
                          {sp}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className={labelClass}>Industry <span className="text-red-500">*</span></label>
                <select value={formData.industry} onChange={(e) => handleChange('industry', e.target.value)} className={inputClass}>
                  <option value="">Select industry...</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
                {formData.industry === 'Other' && (
                  <input type="text" value={formData.industryOther} onChange={(e) => handleChange('industryOther', e.target.value)}
                    placeholder="Specify industry" className={`${inputClass} mt-2`} />
                )}
              </div>
              <div>
                <label className={labelClass}>Location <span className="text-red-500">*</span></label>
                <input type="text" value={formData.location} onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="e.g. Manchester, UK" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Website <span className="text-slate-400 font-normal">(optional)</span></label>
                <input type="text" value={formData.website} onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="e.g. www.thecornercafe.co.uk" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Primary Service/Product to Market</label>
                <input type="text" value={brandDna.primaryService} onChange={(e) => handleBrandDna('primaryService', e.target.value)}
                  placeholder="e.g. Signature lattes & pastries" className={inputClass} />
              </div>
            </div>
          </div>

          {/* Ideal Customer */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="text-xs font-bold text-slate-800 mb-3 uppercase tracking-wider">
              <i className="fas fa-user-circle text-blue-600 mr-1.5"></i>Ideal Customer
              <span className="text-slate-400 font-normal normal-case ml-2">Who are you trying to reach?</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className={labelClass}>Age Range</label>
                <input type="text" value={brandDna.idealCustomer.ageRange} onChange={(e) => handleIdealCustomer('ageRange', e.target.value)}
                  placeholder="e.g. 25-45" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Gender</label>
                <select value={brandDna.idealCustomer.gender} onChange={(e) => handleIdealCustomer('gender', e.target.value)} className={inputClass}>
                  <option value="">Any</option>
                  <option value="female">Primarily Female</option>
                  <option value="male">Primarily Male</option>
                  <option value="all">All Genders</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Lifestyle</label>
                <input type="text" value={brandDna.idealCustomer.lifestyle} onChange={(e) => handleIdealCustomer('lifestyle', e.target.value)}
                  placeholder="e.g. Health-conscious" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Location</label>
                <input type="text" value={brandDna.idealCustomer.location} onChange={(e) => handleIdealCustomer('location', e.target.value)}
                  placeholder="e.g. Local, within 10 miles" className={inputClass} />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={() => setActiveSection(2)} className="px-5 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
              Next: Visual Brand <i className="fas fa-arrow-right ml-1.5"></i>
            </button>
          </div>
        </div>
      )}

      {/* ============ SECTION 2: Visual Brand Assets ============ */}
      {activeSection === 2 && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded bg-purple-600 text-white flex items-center justify-center text-[10px] font-bold">2</div>
              <h2 className="text-sm font-bold text-slate-800">Visual Brand Assets</h2>
              <span className="text-[10px] text-slate-400 ml-auto">Your visual identity ensures consistent content</span>
            </div>

            {/* Logo Upload */}
            <div className="mb-4">
              <label className={labelClass}>Business Logo <span className="text-slate-400 font-normal">(PNG preferred)</span></label>
              <div className="flex items-center gap-3">
                {brandDna.logoUrl ? (
                  <div className="w-16 h-16 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                    {isDeadPath(brandDna.logoUrl) ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-lg p-1">
                        <i className="fas fa-exclamation-triangle text-red-400 text-sm mb-0.5"></i>
                        <span className="text-[8px] text-red-500 font-bold text-center leading-tight">Re-upload needed</span>
                      </div>
                    ) : (
                      <img src={getFullUrl(brandDna.logoUrl)} alt="Logo" className="max-w-full max-h-full object-contain" />
                    )}
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center">
                    <i className="fas fa-image text-slate-300 text-lg"></i>
                  </div>
                )}
                <div>
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  <button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                    className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                    {uploadingLogo ? <><i className="fas fa-spinner fa-spin mr-1"></i>Uploading...</> : <><i className="fas fa-upload mr-1"></i>Upload Logo</>}
                  </button>
                  {brandDna.logoUrl && <p className="text-[10px] text-green-600 mt-1"><i className="fas fa-check mr-1"></i>Logo uploaded</p>}
                </div>
              </div>
            </div>

            {/* Colour Palette */}
            <div className="mb-4">
              <label className={labelClass}>Brand Colours <span className="text-slate-400 font-normal">(click to change)</span></label>
              <div className="flex gap-3">
                {[
                  { key: 'primaryColour', label: 'Primary' },
                  { key: 'secondaryColour', label: 'Secondary' },
                  { key: 'accentColour', label: 'Accent' }
                ].map(c => (
                  <div key={c.key} className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <input type="color" value={brandDna[c.key]} onChange={(e) => handleBrandDna(c.key, e.target.value)}
                        className="w-8 h-8 rounded border border-slate-300 cursor-pointer" />
                      <div>
                        <p className="text-[10px] font-semibold text-slate-600">{c.label}</p>
                        <input type="text" value={brandDna[c.key]} onChange={(e) => handleBrandDna(c.key, e.target.value)}
                          className="text-[11px] text-slate-500 w-20 px-1 py-0.5 border border-slate-200 rounded font-mono" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Colour Preview Strip */}
              <div className="flex h-6 rounded-lg overflow-hidden mt-2 border border-slate-200">
                <div className="flex-1" style={{ backgroundColor: brandDna.primaryColour }}></div>
                <div className="flex-1" style={{ backgroundColor: brandDna.secondaryColour }}></div>
                <div className="flex-1" style={{ backgroundColor: brandDna.accentColour }}></div>
              </div>
            </div>

          </div>

          {/* Style Examples - moved here from old Section 3 */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="text-xs font-bold text-slate-800 mb-1 uppercase tracking-wider">
              <i className="fas fa-images text-green-600 mr-1.5"></i>Example Posts You Love
              <span className="text-slate-400 font-normal normal-case ml-2">(optional — upload up to 3)</span>
            </h3>
            <p className="text-[10px] text-slate-400 mb-3">
              Upload 1–3 example posts from brands you admire.{' '}
              <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-600 font-semibold px-2 py-0.5 rounded-full border border-blue-100">
                <i className="fas fa-robot text-[9px]"></i> Our AI will learn from these
              </span>
            </p>
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map(i => (
                <div key={i} className="relative">
                  <input ref={styleInputRefs[i]} type="file" accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => handleStyleImageUpload(i, e)} className="hidden" />
                  {brandDna.styleExamples[i] ? (
                    <div className="relative group">
                      <div className="aspect-square rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                        {isDeadPath(brandDna.styleExamples[i]) ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 gap-1 p-2">
                            <i className="fas fa-exclamation-triangle text-red-400 text-xl"></i>
                            <span className="text-[9px] text-red-600 font-bold text-center leading-tight">Re-upload needed</span>
                            <span className="text-[8px] text-red-400 text-center leading-tight">Click to re-upload</span>
                          </div>
                        ) : (
                          <img src={getFullUrl(brandDna.styleExamples[i])} alt={`Style ${i + 1}`}
                            className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                        <button onClick={() => styleInputRefs[i].current?.click()}
                          className="p-1.5 bg-white rounded-full text-slate-700 hover:text-blue-600 transition-colors">
                          <i className="fas fa-sync-alt text-xs"></i>
                        </button>
                        <button onClick={() => removeStyleImage(i)}
                          className="p-1.5 bg-white rounded-full text-slate-700 hover:text-red-600 transition-colors">
                          <i className="fas fa-trash text-xs"></i>
                        </button>
                      </div>
                      <div className={`absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center ${isDeadPath(brandDna.styleExamples[i]) ? 'bg-red-400' : 'bg-green-500'}`}>
                        <i className={`text-white text-[7px] ${isDeadPath(brandDna.styleExamples[i]) ? 'fas fa-exclamation' : 'fas fa-check'}`}></i>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => styleInputRefs[i].current?.click()} disabled={uploadingStyle === i}
                      className="w-full aspect-square rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50 transition-all flex flex-col items-center justify-center gap-1">
                      {uploadingStyle === i ? (
                        <i className="fas fa-spinner fa-spin text-blue-500 text-lg"></i>
                      ) : (
                        <>
                          <i className="fas fa-cloud-upload-alt text-slate-300 text-lg"></i>
                          <span className="text-[10px] text-slate-400 font-medium">Upload {i + 1}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
            {brandDna.styleExamples.filter(Boolean).length > 0 && (
              <p className="text-[11px] text-green-600 mt-1.5 font-medium">
                <i className="fas fa-check-circle mr-1"></i>{brandDna.styleExamples.filter(Boolean).length} image{brandDna.styleExamples.filter(Boolean).length > 1 ? 's' : ''} uploaded
              </p>
            )}
          </div>

          <div className="flex justify-between">
            <button onClick={() => setActiveSection(1)} className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
              <i className="fas fa-arrow-left mr-1.5"></i>Back
            </button>
            <button onClick={() => setActiveSection(3)} className="px-5 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
              Next: Platforms &amp; Voice <i className="fas fa-arrow-right ml-1.5"></i>
            </button>
          </div>
        </div>
      )}


      {/* ============ SECTION 3: Platforms, Format & Tone ============ */}
      {activeSection === 3 && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold">3</div>
              <h2 className="text-sm font-bold text-slate-800">Platforms & Format Priorities</h2>
            </div>

            {/* Platform Selection */}
            <div className="mb-4">
              <label className={labelClass}>Target Platforms <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PLATFORMS.map(platform => {
                  const selected = formData.preferredPlatforms.includes(platform.id);
                  return (
                    <button key={platform.id} type="button" onClick={() => togglePlatform(platform.id)}
                      className={`relative flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all ${
                        selected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}>
                      {selected && (
                        <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-blue-600 rounded-full flex items-center justify-center">
                          <i className="fas fa-check text-white text-[7px]"></i>
                        </div>
                      )}
                      <i className={`${platform.icon} text-sm ${selected ? 'text-blue-600' : 'text-slate-400'}`}></i>
                      <span className={`text-xs font-medium ${selected ? 'text-blue-700' : 'text-slate-600'}`}>{platform.name}</span>
                    </button>
                  );
                })}
              </div>
              {formData.preferredPlatforms.length > 0 && (
                <p className="text-[11px] text-green-600 mt-1.5 font-medium">
                  <i className="fas fa-check-circle mr-1"></i>{formData.preferredPlatforms.length} platform{formData.preferredPlatforms.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>

            {/* Reels Priority */}
            <div className="mb-4">
              <label className={labelClass}>Format Priority</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'reels_first', label: 'Reels & Shorts First', icon: 'fas fa-film' },
                  { id: 'static_first', label: 'Static Posts First', icon: 'fas fa-image' },
                  { id: 'both', label: 'Mix of Both', icon: 'fas fa-th-large' }
                ].map(opt => (
                  <button key={opt.id} onClick={() => handleBrandDna('reelsPriority', opt.id)}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      brandDna.reelsPriority === opt.id ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'
                    }`}>
                    <i className={`${opt.icon} text-sm mb-1 ${brandDna.reelsPriority === opt.id ? 'text-blue-600' : 'text-slate-400'}`}></i>
                    <p className="text-[10px] font-semibold text-slate-700">{opt.label}</p>
                  </button>
                ))}
              </div>
              {hasExistingCalendar && brandDna.reelsPriority !== savedReelsPriority && brandDna.reelsPriority && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <i className="fas fa-exclamation-triangle text-[10px] flex-shrink-0"></i>
                  <span>Changing Format Priority will reset your content calendar when you save.</span>
                </div>
              )}
            </div>

            {/* Posting Frequency */}
            <div className="mb-4">
              <label className={labelClass}>Posting Frequency</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    id: 'weekdays',
                    label: '5 Days a Week',
                    desc: 'Monday – Friday',
                    icon: 'fas fa-briefcase',
                    note: 'Sat & Sun left empty'
                  },
                  {
                    id: 'daily',
                    label: '7 Days a Week',
                    desc: 'Every day including weekends',
                    icon: 'fas fa-calendar-check',
                    note: 'All 7 days covered'
                  }
                ].map(opt => (
                  <button key={opt.id} onClick={() => handleBrandDna('postingFrequency', opt.id)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      brandDna.postingFrequency === opt.id
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${opt.icon} text-sm ${brandDna.postingFrequency === opt.id ? 'text-blue-600' : 'text-slate-400'}`}></i>
                      <p className={`text-xs font-bold ${brandDna.postingFrequency === opt.id ? 'text-blue-700' : 'text-slate-700'}`}>{opt.label}</p>
                    </div>
                    <p className="text-[10px] text-slate-500">{opt.desc}</p>
                    <p className={`text-[10px] font-medium mt-0.5 ${brandDna.postingFrequency === opt.id ? 'text-blue-500' : 'text-slate-400'}`}>{opt.note}</p>
                  </button>
                ))}
              </div>
              {hasExistingCalendar && brandDna.postingFrequency !== savedPostingFrequency && brandDna.postingFrequency && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <i className="fas fa-exclamation-triangle text-[10px] flex-shrink-0"></i>
                  <span>Changing Posting Frequency will reset your content calendar when you save.</span>
                </div>
              )}
            </div>

          </div>

          {/* Tone of Voice — merged from old Section 5 */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded bg-slate-700 text-white flex items-center justify-center text-[10px]">
                <i className="fas fa-microphone text-[9px]"></i>
              </div>
              <h2 className="text-sm font-bold text-slate-800">Tone of Voice</h2>
              <span className="text-[10px] text-slate-400 ml-auto">How your captions should sound</span>
            </div>

            {/* Caption Tone */}
            <div className="mb-4">
              <label className={labelClass}>Caption Tone <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-5 gap-2">
                {CAPTION_TONES.map(t => {
                  const selected = brandDna.captionTone.includes(t.id);
                  return (
                    <button key={t.id} onClick={() => toggleCaptionTone(t.id)}
                      className={`relative p-2.5 rounded-lg border text-center transition-all ${
                        selected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                      {selected && (
                        <div className="absolute top-1 right-1 w-3.5 h-3.5 bg-blue-600 rounded-full flex items-center justify-center">
                          <i className="fas fa-check text-white text-[7px]"></i>
                        </div>
                      )}
                      <i className={`${t.icon} text-sm mb-1 ${selected ? 'text-blue-600' : 'text-slate-400'}`}></i>
                      <p className="text-[10px] font-semibold text-slate-700">{t.label}</p>
                    </button>
                  );
                })}
              </div>
              {brandDna.captionTone.length > 0 && (
                <p className="text-[11px] text-green-600 mt-1.5 font-medium">
                  <i className="fas fa-check-circle mr-1"></i>{brandDna.captionTone.length} tone{brandDna.captionTone.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>

            {/* Emojis */}
            <div className="mb-4">
              <label className={labelClass}>Use Emojis in Posts?</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'yes', label: 'Yes, Often' },
                  { id: 'occasionally', label: 'Occasionally' },
                  { id: 'no', label: 'Never' }
                ].map(opt => (
                  <button key={opt.id} onClick={() => handleBrandDna('useEmojis', opt.id)}
                    className={`py-2.5 rounded-lg border text-center transition-all ${
                      brandDna.useEmojis === opt.id ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'
                    }`}>
                    <span className="text-xs font-semibold text-slate-700">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Restrictions */}
            <div>
              <label className={labelClass}>Content Restrictions <span className="text-slate-400 font-normal">(words, phrases, or topics to avoid)</span></label>
              <textarea value={brandDna.contentRestrictions} onChange={(e) => handleBrandDna('contentRestrictions', e.target.value)}
                placeholder="e.g. Never use slang, avoid mentioning competitor names, no political topics..."
                rows={2} className={inputClass} />
            </div>
          </div>

          {/* Brand DNA Summary Card */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
            <h3 className="text-xs font-bold text-white mb-3 uppercase tracking-wider">
              <i className="fas fa-dna text-blue-400 mr-1.5"></i>Brand DNA Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
              <div><span className="text-slate-400">Business:</span> <span className="text-white font-medium">{formData.businessName || '—'}</span></div>
              <div><span className="text-slate-400">Specialty:</span> <span className="text-white font-medium">{brandDna.businessSpecialty || formData.industry || '—'}</span></div>
              <div><span className="text-slate-400">Location:</span> <span className="text-white font-medium">{formData.location || '—'}</span></div>
              <div className="flex items-center gap-1">
                <span className="text-slate-400">Colours:</span>
                {[brandDna.primaryColour, brandDna.secondaryColour, brandDna.accentColour].map((c, i) => (
                  <div key={i} className="w-4 h-4 rounded-sm border border-slate-600" style={{ backgroundColor: c }}></div>
                ))}
              </div>
              <div><span className="text-slate-400">Platforms:</span> <span className="text-white font-medium">{formData.preferredPlatforms.join(', ') || '—'}</span></div>
              <div><span className="text-slate-400">Frequency:</span> <span className="text-white font-medium">{brandDna.postingFrequency || '—'}</span></div>
              <div><span className="text-slate-400">Tone:</span> <span className="text-white font-medium">{brandDna.captionTone.length > 0 ? brandDna.captionTone.join(', ') : '—'}</span></div>
              <div><span className="text-slate-400">Style Images:</span> <span className="text-white font-medium">{brandDna.styleExamples.filter(Boolean).length} uploaded</span></div>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setActiveSection(2)} className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
              <i className="fas fa-arrow-left mr-1.5"></i>Back
            </button>
            <button onClick={handleSubmit} disabled={saving || !isFormValid()}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                isFormValid() && !saving
                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}>
              {saving ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                <><i className="fas fa-check-circle"></i> Save Brand DNA & Continue</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
